import { httpClient } from '@/shared/api/httpClient';
import { classifyApiError } from '@/shared/api/classifyApiError';
import type { Timelog } from '../../domain/entities/Timelog';
import type { TimelogDraft } from '../../domain/entities/TimelogDraft';
import type { TimelogListResponseDTO } from '../dto/TimelogDTO';
import { toTimelog, toCreateTimelogRequest } from '../mappers/timelogMapper';

const PERSON_USER_BUG_MARKER = 'personUser';

/**
 * Normalizes a date input to `YYYY-MM-DD` for the backend `where[date_pg]`
 * query parameter.
 *
 * The backend builds raw SQL `WHERE DATE_PG BETWEEN <from> AND <to>` where
 * `DATE_PG` is a SQL DATE column (not a TIMESTAMP). Passing a full ISO
 * timestamp like `2026-06-02T08:00:35.310Z` causes PostgreSQL to fail with
 * `SQLSTATE[42601]: SYNTAX ERROR: TRAILING JUNK AFTER NUMERIC LITERAL`
 * (HTTP 500). This helper truncates ISO timestamps to date-only form so
 * every caller — including the background scheduler that uses
 * `new Date().toISOString()` — produces a valid query.
 */
function toDateOnly(input: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
    return input.slice(0, 10);
  }
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Invalid date string for where[date_pg]: ${input}`);
  }
  return parsed.toISOString().slice(0, 10);
}

export class RemoteTimelogRepository {
  async create(
    draft: TimelogDraft,
  ): Promise<Timelog | { virtualSuccess: true; localId: string }> {
    const payload = toCreateTimelogRequest(draft);

    try {
      const res = await httpClient.post<number>(
        `/v1/clock/app/entity/${draft.idEntity}/timelogs`,
        payload,
      );
      // API returns plain integer on 201
      const serverId = typeof res.data === 'number' ? res.data : 0;
      return {
        idTimelog:        serverId,
        idPerson:         0,
        idEntity:         draft.idEntity,
        // draft.idDevice is string | null; Timelog.idDevice is number | null
        idDevice:         draft.idDevice !== null ? (Number(draft.idDevice) || null) : null,
        datetime:         draft.datetime,
        accessType:       draft.accessType,
        verificationMode: draft.verificationMode,
        verificationResult: true,
        isOffline:        false,
        coordinates:      null,
        employee:         null,
      };
    } catch (err) {
      const apiErr = classifyApiError(err);
      // Bug 1: id_device + photo triggers "Undefined variable: personUser" → retry without id_device
      if (
        apiErr.kind === 'backend_bug' ||
        (apiErr.kind === 'server' && String(apiErr.message).includes(PERSON_USER_BUG_MARKER))
      ) {
        if (payload.id_device !== null) {
          const retryPayload = { ...payload, id_device: null };
          try {
            const retryRes = await httpClient.post<number>(
              `/v1/clock/app/entity/${draft.idEntity}/timelogs`,
              retryPayload,
            );
            const serverId = typeof retryRes.data === 'number' ? retryRes.data : 0;
            return {
              idTimelog:        serverId,
              idPerson:         0,
              idEntity:         draft.idEntity,
              idDevice:         null,
              datetime:         draft.datetime,
              accessType:       draft.accessType,
              verificationMode: draft.verificationMode,
              verificationResult: true,
              isOffline:        false,
              coordinates:      null,
              employee:         null,
            };
          } catch {
            // fall through to virtualSuccess
          }
        }
        return { virtualSuccess: true, localId: draft.localId };
      }
      throw apiErr;
    }
  }

  async listByAccessRole(
    accessRoleId: number,
    from: string,
    to: string,
  ): Promise<Timelog[]> {
    try {
      const res = await httpClient.get<TimelogListResponseDTO>(
        `/v1/clock/app/${accessRoleId}/timelogs`,
        {
          params: { 'where[date_pg]': `${toDateOnly(from)},${toDateOnly(to)}` },
          // 5s timeout for offline-first fallback — default 120s is too slow
          timeout: 5_000,
        },
      );
      // Sort descending by datetime client-side — `orderby=desc` causes backend SQL error
      // (generates "ORDER BY DESC" without column name, code 42601)
      const timelogs = res.data.data.results.map(toTimelog);
      return timelogs.sort((a, b) => b.datetime.localeCompare(a.datetime));
    } catch (err) {
      throw classifyApiError(err);
    }
  }
}
